import React, { useState, useMemo, useEffect } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import NColumnTable from "@/charts/NColumnTable";
import Sidebar from "@/layout/Sidebar";
import TopNavbar from "@/layout/TopNavbar";
import useGetQuery from "@/hooks/useGetQuery";
import { useQueryClient } from "@tanstack/react-query";
import CustomDropdown from "@/components/ui/CustomDropdown";
import BrandButton from "@/components/ui/BrandButton";
import { showApiError, showSuccess, showWarning } from "@/utils";
import QueryResearchResult from "@/pages/QueryResearch/QueryResearchResult";
import "./page.css";

const roundSearchSignal = (val) => {
  if (val == null) return "-";
  const num = Number(val);
  return isNaN(num) ? val : Math.round(num * 100) / 100;
};

function EditQueryPage() {
  const { t, i18n } = useTranslation();
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  const queryClient = useQueryClient();
  const queriesUrl = `${BACKEND_URL}/api/v1/generated-queries`;
  const [isPermanentlyExpanded, setIsPermanentlyExpanded] = useState(true);
  const [querySearchTerm, setQuerySearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all"); // 'all', 'active', 'queued_for_processing'
  const [selectedQueries, setSelectedQueries] = useState(new Set());
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingQuery, setEditingQuery] = useState(null);
  const [editFormData, setEditFormData] = useState({
    query: "",
    is_branded: "false",
    funnel_stage: "top",
  });
  const [showAddModal, setShowAddModal] = useState(false);
  const [addMethod, setAddMethod] = useState("text"); // 'text' or 'csv'
  const [newQueriesText, setNewQueriesText] = useState("");
  const [csvFile, setCsvFile] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isAdding, setIsAdding] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // Historical queries states
  const [historicalQueries, setHistoricalQueries] = useState([]);
  const [addingHistoricalQuery, setAddingHistoricalQuery] = useState(null);

  // History modal states
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [historyQuery, setHistoryQuery] = useState("");
  const [historyTimestamps, setHistoryTimestamps] = useState([]);
  const [historyTimestampsLoading, setHistoryTimestampsLoading] =
    useState(false);
  const [historyTimestampsError, setHistoryTimestampsError] = useState(null);
  const [historySelectedQueryId, setHistorySelectedQueryId] = useState("");
  const [historyResult, setHistoryResult] = useState(null);
  const [historyResultLoading, setHistoryResultLoading] = useState(false);
  const [historyResultError, setHistoryResultError] = useState(null);

  const handleOpenHistoryModal = React.useCallback(
    async (queryText) => {
      setHistoryQuery(queryText);
      setShowHistoryModal(true);
      setHistoryTimestamps([]);
      setHistoryTimestampsError(null);
      setHistorySelectedQueryId("");
      setHistoryResult(null);
      setHistoryResultError(null);
      setHistoryTimestampsLoading(true);

      try {
        const response = await fetch(
          `${BACKEND_URL}/api/v1/historical-queries/geo-dashboard/query-timestamps?query=${encodeURIComponent(
            queryText,
          )}&limit=100`,
          {
            method: "GET",
            credentials: "include",
          },
        );

        if (!response.ok) {
          throw new Error(`Failed to fetch timestamps: ${response.statusText}`);
        }

        const data = await response.json();
        setHistoryTimestamps(data?.data || []);
      } catch (error) {
        console.error("Error fetching historical timestamps:", error);
        setHistoryTimestampsError(error);
      } finally {
        setHistoryTimestampsLoading(false);
      }
    },
    [BACKEND_URL],
  );

  const handleCloseHistoryModal = React.useCallback(() => {
    setShowHistoryModal(false);
    setHistoryQuery("");
    setHistoryTimestamps([]);
    setHistoryTimestampsError(null);
    setHistorySelectedQueryId("");
    setHistoryResult(null);
    setHistoryResultError(null);
  }, []);

  const handleHistoryDateSelect = React.useCallback(
    async (queryId) => {
      setHistorySelectedQueryId(queryId);
      setHistoryResult(null);
      setHistoryResultError(null);

      if (!queryId) return;

      setHistoryResultLoading(true);
      try {
        const response = await fetch(
          `${BACKEND_URL}/api/v1/historical-queries/geo-dashboard/${queryId}`,
          {
            method: "GET",
            credentials: "include",
          },
        );

        if (!response.ok) {
          throw new Error(
            `Failed to fetch GEO Dashboard result: ${response.statusText}`,
          );
        }

        const data = await response.json();
        setHistoryResult(data);
      } catch (error) {
        console.error("Error fetching historical results:", error);
        setHistoryResultError(error);
      } finally {
        setHistoryResultLoading(false);
      }
    },
    [BACKEND_URL],
  );

  const [queriesResponse, queriesLoaded, queriesError] = useGetQuery(
    queriesUrl,
    {
      staleTime: 0, // always revalidate after mutations via invalidateQueries
    },
  );

  const toggleSidebar = () => {
    setIsPermanentlyExpanded(!isPermanentlyExpanded);
  };

  // Fetch historical queries and filter out already existing ones
  useEffect(() => {
    // Only fetch if queriesResponse has been loaded
    if (!queriesResponse || !queriesLoaded) {
      return;
    }

    const fetchHistoricalQueries = async () => {
      try {
        // Fetch query research history
        const historyResponse = await fetch(
          `${BACKEND_URL}/api/v1/query-research/history?limit=50`,
          {
            method: "GET",
            credentials: "include",
          },
        );
        if (!historyResponse.ok) {
          console.error(
            `Failed to fetch historical queries: ${historyResponse.status} ${historyResponse.statusText}`,
          );
          setHistoricalQueries([]);
          return;
        }
        const historyData = await historyResponse.json();

        if (historyData.success && Array.isArray(historyData.history)) {
          // Extract existing queries from generated_queries
          const existingQuerySet = new Set();

          if (queriesResponse?.data?.generated_queries) {
            queriesResponse.data.generated_queries.forEach((item) => {
              if (item.query) {
                existingQuerySet.add(item.query);
              }
            });
          }

          // Filter out already existing queries
          const filteredQueries = historyData.history.filter(
            (query) => !existingQuerySet.has(query),
          );

          setHistoricalQueries(filteredQueries);
        }
      } catch (error) {
        console.error("Error fetching historical queries:", error);
        setHistoricalQueries([]);
      }
    };

    fetchHistoricalQueries();
  }, [BACKEND_URL, queriesResponse, queriesLoaded]);

  // Handler for adding historical query to generated queries
  const handleAddHistoricalQuery = async (query) => {
    setAddingHistoricalQuery(query);

    try {
      const response = await fetch(
        `${BACKEND_URL}/api/v1/generated-queries/insert`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          credentials: "include",
          body: JSON.stringify({ queries: [query] }),
        },
      );

      if (!response.ok) {
        console.error(
          `Failed to add historical query: ${response.status} ${response.statusText}`,
        );
        showApiError(
          t("promptLibrary.addPromptError", "Failed to add prompt. Please try again."),
        );
        return;
      }

      const data = await response.json();

      if (data.success) {
        // Remove from historical queries list
        setHistoricalQueries((prev) => prev.filter((q) => q !== query));
        queryClient.invalidateQueries({ queryKey: [queriesUrl] });
      } else {
        showApiError(
          data.message ||
            t("promptLibrary.addPromptError", "Failed to add prompt"),
        );
      }
    } catch (error) {
      console.error("Error adding historical query:", error);
      showApiError(
        t("promptLibrary.addPromptError", "Failed to add prompt. Please try again."),
      );
    } finally {
      setAddingHistoricalQuery(null);
    }
  };

  const handleSelectQuery = React.useCallback((queryId) => {
    setSelectedQueries((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(queryId)) {
        newSet.delete(queryId);
      } else {
        newSet.add(queryId);
      }
      return newSet;
    });
  }, []);

  const handleEdit = React.useCallback((query) => {
    setEditingQuery(query);
    setEditFormData({
      query: query.query || "",
      is_branded: query.is_branded === true ? "true" : "false",
      funnel_stage: query.funnel_stage || "top",
    });
    setShowEditModal(true);
  }, []);

  const handleCloseEditModal = () => {
    setShowEditModal(false);
    setEditingQuery(null);
    setEditFormData({
      query: "",
      is_branded: "false",
      funnel_stage: "top",
    });
  };

  const handleEditFormChange = (field, value) => {
    setEditFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSaveEdit = async () => {
    if (!editingQuery) return;

    setIsEditing(true);
    try {
      const payload = {
        generated_query_id: editingQuery.generated_query_id,
        query: editFormData.query,
        is_branded: editFormData.is_branded === "true",
        funnel_stage: editFormData.funnel_stage,
      };

      const response = await fetch(
        `${BACKEND_URL}/api/v1/generated-queries/edit`,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
          },
          credentials: "include",
          body: JSON.stringify(payload),
        },
      );

      if (!response.ok) {
        throw new Error("Failed to update query");
      }

      await response.json();

      queryClient.invalidateQueries({ queryKey: [queriesUrl] });
      handleCloseEditModal();
    } catch (error) {
      console.error("Error updating query:", error);
      showApiError(
        t(
          "promptLibrary.updatePromptError",
          "Failed to update prompt. Please try again.",
        ),
      );
    } finally {
      setIsEditing(false);
    }
  };

  const handleOpenAddModal = () => {
    setShowAddModal(true);
    setAddMethod("text");
    setNewQueriesText("");
    setCsvFile(null);
  };

  const handleCloseAddModal = () => {
    setShowAddModal(false);
    setAddMethod("text");
    setNewQueriesText("");
    setCsvFile(null);
  };

  const handleCsvFileChange = (e) => {
    const file = e.target.files[0];
    if (file && file.name.endsWith(".csv")) {
      setCsvFile(file);
    } else {
      showApiError(
        t(
          "promptLibrary.validCsvWarning",
          "Please select a valid CSV file",
        ),
      );
      e.target.value = null;
    }
  };

  const handleAddQueries = async () => {
    setIsAdding(true);
    try {
      if (addMethod === "text") {
        // Add queries from text input
        const queries = newQueriesText
          .split("\n")
          .map((q) => q.trim())
          .filter((q) => q.length > 0);

        if (queries.length === 0) {
          showWarning(
            t(
              "promptLibrary.enterAtLeastOnePromptWarning",
              "Please enter at least one prompt",
            ),
          );
          setIsAdding(false);
          return;
        }

        const response = await fetch(
          `${BACKEND_URL}/api/v1/generated-queries/insert`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            credentials: "include",
            body: JSON.stringify({ queries }),
          },
        );

        if (!response.ok) {
          throw new Error("Failed to insert queries");
        }

        const result = await response.json();
        showSuccess(
          result.message ||
            t(
              "promptLibrary.promptAddedSuccess",
              "Prompt(s) added successfully.",
            ),
        );
      } else {
        // Add queries from CSV file
        if (!csvFile) {
          showWarning(
            t(
              "promptLibrary.selectCsvWarning",
              "Please select a CSV file",
            ),
          );
          setIsAdding(false);
          return;
        }

        const formData = new FormData();
        formData.append("file", csvFile);

        const response = await fetch(
          `${BACKEND_URL}/api/v1/generated-queries/insert-from-file`,
          {
            method: "POST",
            credentials: "include",
            body: formData,
          },
        );

        if (!response.ok) {
          throw new Error("Failed to upload CSV");
        }

        const result = await response.json();
        showSuccess(
          result.message ||
            t(
              "promptLibrary.promptAddedSuccess",
              "Prompt(s) added successfully.",
            ),
        );
      }

      queryClient.invalidateQueries({ queryKey: [queriesUrl] });
      handleCloseAddModal();
    } catch (error) {
      console.error("Error adding queries:", error);
      showApiError(
        t(
          "promptLibrary.addPromptError",
          "Failed to add prompts. Please try again.",
        ),
      );
    } finally {
      setIsAdding(false);
    }
  };

  const handleDeleteClick = () => {
    if (selectedQueries.size === 0) {
      showWarning(
        t(
          "promptLibrary.selectPromptsToDeleteWarning",
          "Please select at least one prompt to delete",
        ),
      );
      return;
    }
    setShowDeleteConfirm(true);
  };

  const handleCloseDeleteConfirm = () => {
    setShowDeleteConfirm(false);
  };

  const handleConfirmDelete = async () => {
    setIsDeleting(true);
    try {
      const generated_query_ids = Array.from(selectedQueries);

      const response = await fetch(
        `${BACKEND_URL}/api/v1/generated-queries/delete`,
        {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
          },
          credentials: "include",
          body: JSON.stringify({ generated_query_ids }),
        },
      );

      if (!response.ok) {
        throw new Error("Failed to delete queries");
      }

      const result = await response.json();
      showSuccess(
        result.message ||
          t(
            "promptLibrary.promptDeletedSuccess",
            "Prompt(s) deleted successfully.",
          ),
      );

      setSelectedQueries(new Set());
      queryClient.invalidateQueries({ queryKey: [queriesUrl] });
      handleCloseDeleteConfirm();
    } catch (error) {
      console.error("Error deleting queries:", error);
      showApiError(
        t(
          "promptLibrary.deletePromptsError",
          "Failed to delete prompts. Please try again.",
        ),
      );
    } finally {
      setIsDeleting(false);
    }
  };

  // Get selected query details for delete confirmation
  const getSelectedQueryDetails = () => {
    const allQueries = queriesResponse?.data?.generated_queries || [];
    return allQueries.filter((q) => selectedQueries.has(q.generated_query_id));
  };

  // Calculate counts for each status
  const statusCounts = useMemo(() => {
    const allQueries = queriesResponse?.data?.generated_queries || [];
    const active = allQueries.filter((q) => q.has_been_processed).length;
    const queued_for_processing = allQueries.filter(
      (q) => !q.has_been_processed,
    ).length;
    return {
      all: allQueries.length,
      active,
      queued_for_processing,
    };
  }, [queriesResponse]);

  // Filter queries based on search term, status filter
  const baseFilteredQueries = useMemo(() => {
    const allQueries = queriesResponse?.data?.generated_queries || [];
    let queries = allQueries;

    // Apply status filter
    if (statusFilter === "active") {
      queries = queries.filter((q) => q.has_been_processed);
    } else if (statusFilter === "queued_for_processing") {
      queries = queries.filter((q) => !q.has_been_processed);
    }

    // Apply search filter
    if (querySearchTerm.trim() && queries.length > 0) {
      queries = queries.filter((query) => {
        const searchLower = querySearchTerm.toLowerCase();
        const queryText = query.query ? query.query.toLowerCase() : "";
        const queryType = query.query_type
          ? query.query_type.toLowerCase()
          : "";
        const funnelStage = query.funnel_stage
          ? query.funnel_stage.toLowerCase()
          : "";
        const isBranded =
          query.is_branded === true
            ? "branded"
            : query.is_branded === false
              ? "non-branded"
              : "";
        const roundedSignal = roundSearchSignal(query.search_signal).toString();
        const searchSignal =
          query.search_signal != null ? query.search_signal.toString() : "";

        return (
          queryText.includes(searchLower) ||
          queryType.includes(searchLower) ||
          funnelStage.includes(searchLower) ||
          isBranded.includes(searchLower) ||
          searchSignal.includes(searchLower) ||
          roundedSignal.includes(searchLower)
        );
      });
    }

    return queries;
  }, [queriesResponse, querySearchTerm, statusFilter]);

  const allFilteredIds = useMemo(
    () => baseFilteredQueries.map((q) => q.generated_query_id),
    [baseFilteredQueries],
  );

  const isAllSelected = useMemo(
    () =>
      allFilteredIds.length > 0 &&
      allFilteredIds.every((id) => selectedQueries.has(id)),
    [allFilteredIds, selectedQueries],
  );

  const isSomeSelected = useMemo(
    () =>
      allFilteredIds.some((id) => selectedQueries.has(id)) && !isAllSelected,
    [allFilteredIds, selectedQueries, isAllSelected],
  );

  const handleSelectAll = React.useCallback(() => {
    setSelectedQueries((prev) => {
      const allSelected =
        allFilteredIds.length > 0 &&
        allFilteredIds.every((id) => prev.has(id));
      if (allSelected) {
        const next = new Set(prev);
        allFilteredIds.forEach((id) => next.delete(id));
        return next;
      }
      return new Set([...prev, ...allFilteredIds]);
    });
  }, [allFilteredIds]);

  // Plain-string version of baseFilteredQueries used for CSV export
  const filteredQueriesExport = useMemo(() => {
    const qtLabel = (qt) => {
      if (qt === "ai_summary" || qt === "real") return "Real";
      if (qt === "synthetic") return "Synthetic";
      return qt || "-";
    };
    return baseFilteredQueries.map((q) => ({
      query: q.query || "",
      search_signal: roundSearchSignal(q.search_signal),
      is_branded:
        q.is_branded === true
          ? "Branded"
          : q.is_branded === false
            ? "Non-Branded"
            : "-",
      funnel_stage: q.funnel_stage || "-",
      query_type: qtLabel(q.query_type),
      status: q.has_been_processed ? "Active" : "Queued for Processing",
    }));
  }, [baseFilteredQueries]);

  const filteredQueries = useMemo(() => {
    // Add UI elements for select button, status badge, and edit button
    return baseFilteredQueries.map((query) => ({
      ...query,
      search_signal: roundSearchSignal(query.search_signal),
      is_branded:
        query.is_branded === true ? (
          <span
            className="badge"
            style={{
              backgroundColor: "var(--badge-branded-bg)",
              color: "var(--badge-branded-text)",
            }}
          >
            {t("promptLibrary.branded", "Branded")}
          </span>
        ) : query.is_branded === false ? (
          <span
            className="badge"
            style={{
              backgroundColor: "var(--badge-unbranded-bg)",
              color: "var(--badge-unbranded-text)",
            }}
          >
            {t("promptLibrary.nonBranded", "Non-Branded")}
          </span>
        ) : (
          "-"
        ),
      funnel_stage: (() => {
        const stage = query.funnel_stage;
        if (!stage) return "-";
        const stageLower = stage.toLowerCase();
        const stageColors = {
          top: { bg: "var(--funnel-top-bg)", color: "var(--funnel-top-text)" },
          middle: {
            bg: "var(--funnel-mid-bg)",
            color: "var(--funnel-mid-text)",
          },
          bottom: {
            bg: "var(--funnel-bottom-bg)",
            color: "var(--funnel-bottom-text)",
          },
        };
        const style = stageColors[stageLower] || {
          bg: "var(--action-primary)",
          color: "white",
        };
        const stageLabel =
          stageLower === "top"
            ? t("tracking.topFunnel", "Top")
            : stageLower === "middle"
              ? t("tracking.middleFunnel", "Middle")
              : stageLower === "bottom"
                ? t("tracking.bottomFunnel", "Bottom")
                : stage;
        return (
          <span
            className="badge"
            style={{ backgroundColor: style.bg, color: style.color }}
          >
            {stageLabel}
          </span>
        );
      })(),
      query_type: (() => {
        const qt = query.query_type;
        if (!qt) return "-";
        const qtConfig = {
          ai_summary: {
            bg: "var(--badge-real-bg)",
            color: "var(--badge-real-text)",
            label: t("promptLibrary.real", "Real"),
          },
          real: {
            bg: "var(--badge-real-bg)",
            color: "var(--badge-real-text)",
            label: t("promptLibrary.real", "Real"),
          },
          synthetic: {
            bg: "var(--badge-synthetic-bg)",
            color: "var(--badge-synthetic-text)",
            label: t("promptLibrary.synthetic", "Synthetic"),
          },
        };
        const config = qtConfig[qt] || {
          bg: "var(--badge-unbranded-bg)",
          color: "var(--badge-unbranded-text)",
          label: qt,
        };
        return (
          <span
            className="badge"
            style={{ backgroundColor: config.bg, color: config.color }}
          >
            {config.label}
          </span>
        );
      })(),
      selectButton: (
        <input
          type="checkbox"
          checked={selectedQueries.has(query.generated_query_id)}
          onChange={() => handleSelectQuery(query.generated_query_id)}
          className="form-check-input"
          style={{ cursor: "pointer" }}
        />
      ),
      seeHistory: (
        <button
          className="btn btn-sm btn-outline-primary border-0"
          onClick={() => handleOpenHistoryModal(query.query)}
          style={{
            color: "var(--brand-color-dark)",
            background: "none",
            padding: 0,
            width: "28px",
            height: "28px",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
          }}
          title={t("promptLibrary.viewHistory", "View Run History")}
        >
          <i className="bi bi-clock-history" style={{ fontSize: "1.1rem" }}></i>
        </button>
      ),
      status: (
        <span
          className="badge"
          style={{
            backgroundColor: query.has_been_processed
              ? "var(--status-active-bg)"
              : "var(--status-queued-bg)",
            color: query.has_been_processed
              ? "var(--status-active-text)"
              : "var(--status-queued-text)",
          }}
        >
          {query.has_been_processed
            ? t("promptLibrary.active", "Active")
            : t("promptLibrary.queuedForProcessing", "Queued for Processing")}
        </span>
      ),
      actions: (
        <button
          className="btn btn-sm btn-outline border-0"
          onClick={() => handleEdit(query)}
          style={{
            color: "var(--action-primary)",
            background: "none",
            padding: 0,
            width: "28px",
            height: "28px",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
          }}
          title={t("promptLibrary.editPrompt", "Edit Prompt")}
        >
          <i className="bi bi-pencil" style={{ fontSize: "1.1rem" }}></i>
        </button>
      ),
    }));
  }, [
    baseFilteredQueries,
    selectedQueries,
    handleSelectQuery,
    handleEdit,
    handleOpenHistoryModal,
    t,
  ]);

  return (
    <div className="app-container">
      <Sidebar
        currentPage={"edit-query"}
        isPermanentlyExpanded={isPermanentlyExpanded}
      />
      <main className="content bg-page">
        <div className="container-fluid p-4">
          <TopNavbar
            isPermanentlyExpanded={isPermanentlyExpanded}
            toggleSidebar={toggleSidebar}
          />

          <div className="mt-4">
            <h3 className="h4 fw-bold text-dark mb-2">
              {t("promptLibrary.title", "Prompt Library")}
            </h3>
            <p className="mt-1 fs-6 text-secondary">
              {t(
                "promptLibrary.subtitle",
                "Manage and organize your prompt dataset for answer engine optimization",
              )}
            </p>

            <div className="mb-3 mt-3 d-flex align-items-center justify-content-between gap-2">
              <div
                className="d-flex align-items-center gap-2"
                style={{ flex: 1 }}
              >
                <input
                  type="text"
                  className="form-control"
                  placeholder={t(
                    "promptLibrary.searchPlaceholder",
                    "Search prompts...",
                  )}
                  value={querySearchTerm}
                  onChange={(e) => setQuerySearchTerm(e.target.value)}
                  style={{ maxWidth: 500 }}
                />

                {/* Status Filter Button Group */}
                <div className="btn-group" role="group">
                  <input
                    type="radio"
                    className="btn-check"
                    name="statusFilter"
                    id="statusAll"
                    autoComplete="off"
                    checked={statusFilter === "all"}
                    onChange={() => setStatusFilter("all")}
                  />
                  <label
                    className="btn btn-outline-primary btn-sm"
                    htmlFor="statusAll"
                    style={{
                      borderColor: "var(--action-primary)",
                      color:
                        statusFilter === "all"
                          ? "white"
                          : "var(--action-primary)",
                      backgroundColor:
                        statusFilter === "all"
                          ? "var(--action-primary)"
                          : "transparent",
                    }}
                  >
                    {t("common.all", "All")} ({statusCounts.all})
                  </label>

                  <input
                    type="radio"
                    className="btn-check"
                    name="statusFilter"
                    id="statusActive"
                    autoComplete="off"
                    checked={statusFilter === "active"}
                    onChange={() => setStatusFilter("active")}
                  />
                  <label
                    className="btn btn-outline-primary btn-sm"
                    htmlFor="statusActive"
                    style={{
                      borderColor: "var(--action-primary)",
                      color:
                        statusFilter === "active"
                          ? "white"
                          : "var(--action-primary)",
                      backgroundColor:
                        statusFilter === "active"
                          ? "var(--action-primary)"
                          : "transparent",
                    }}
                  >
                    {t("promptLibrary.active", "Active")} ({statusCounts.active})
                  </label>

                  <input
                    type="radio"
                    className="btn-check"
                    name="statusFilter"
                    id="statusQueued"
                    autoComplete="off"
                    checked={statusFilter === "queued_for_processing"}
                    onChange={() => setStatusFilter("queued_for_processing")}
                  />
                  <label
                    className="btn btn-outline-primary btn-sm"
                    htmlFor="statusQueued"
                    style={{
                      borderColor: "var(--action-primary)",
                      color:
                        statusFilter === "queued_for_processing"
                          ? "white"
                          : "var(--action-primary)",
                      backgroundColor:
                        statusFilter === "queued_for_processing"
                          ? "var(--action-primary)"
                          : "transparent",
                    }}
                  >
                    {t("promptLibrary.queued", "Queued for Processing")} ({statusCounts.queued_for_processing})
                  </label>
                </div>
              </div>

              <div className="d-flex align-items-center gap-2">
                {selectedQueries.size > 0 && (
                  <button
                    className="btn btn-danger"
                    onClick={handleDeleteClick}
                  >
                    <i className="bi bi-trash"></i> {t("common.delete", "Delete")} (
                    {selectedQueries.size})
                  </button>
                )}
                <BrandButton onClick={handleOpenAddModal}>
                  <i className="bi bi-plus-circle me-2"></i> {t("promptLibrary.addPrompts", "Add Prompts")}
                </BrandButton>
              </div>
            </div>

            <NColumnTable
              data={filteredQueries}
              exportData={filteredQueriesExport}
              title={t("dashboard.prompts", "Prompts")}
              isLoaded={queriesLoaded}
              error={queriesError}
              n={9}
              columnKeys={[
                "selectButton",
                "query",
                "seeHistory",
                "search_signal",
                "is_branded",
                "funnel_stage",
                "query_type",
                "status",
                "actions",
              ]}
              columnTitles={[
                <input
                  key="select-all-checkbox"
                  type="checkbox"
                  className="form-check-input"
                  style={{ cursor: "pointer" }}
                  checked={isAllSelected}
                  ref={(el) => {
                    if (el) {
                      el.indeterminate = isSomeSelected;
                    }
                  }}
                  onChange={handleSelectAll}
                  disabled={!queriesLoaded || allFilteredIds.length === 0}
                  aria-label={
                    isAllSelected
                      ? t("common.deselectAll", "Deselect All")
                      : t("common.selectAll", "Select All")
                  }
                  title={
                    isAllSelected
                      ? t("common.deselectAll", "Deselect All")
                      : t("common.selectAll", "Select All")
                  }
                  data-testid="select-all-checkbox"
                />,
                "",
                t("promptLibrary.history", "History"),
                t("promptLibrary.searchSignal", "Search Signal"),
                t("promptLibrary.isBranded", "Branded"),
                t("promptLibrary.funnelStage", "Funnel Stage"),
                t("contentOptimizer.queryType", "Prompt Type"),
                t("common.status", "Status"),
                t("common.edit", "Edit"),
              ]}
              columnRatio="3-31-8-10-11-10-11-13-3"
              columnAlignment="c-l-c-c-c-c-c-c-c"
              wrapText={true}
              noDataMessage={
                querySearchTerm.trim() ? (
                  <span>
                    {t("promptLibrary.noPromptsFound", "No prompts found.")}{" "}
                    <Link
                      to="/archived-prompt-gd"
                      style={{ color: "var(--action-primary)" }}
                    >
                      {t("dashboard.clickHere", "check the Prompts Archive")}
                    </Link>
                    .
                  </span>
                ) : (
                  t("common.noDataGeneric", "No data available")
                )
              }
            />

            {/* Historical Queries Section */}
            {historicalQueries.length > 0 && queriesLoaded && (
              <div className="mb-2 w-50 mt-4">
                <div className="section-header mb-3">
                  <h4 className="h5 text-dark mb-0 optimize-heading">
                    {t("promptLibrary.suggestedPrompts", "Suggested Prompts")}
                  </h4>
                </div>
                <p className="mt-1 fs-6 text-secondary mb-3">
                  {t(
                    "promptLibrary.suggestedPromptsSubtitle",
                    "Add your searched prompts to the prompt list for weekly processing",
                  )}
                </p>
                <div>
                  <ul>
                    {historicalQueries.slice(0, 5).map((query) => (
                      <li key={`historical-query-${query}`} className="mb-2">
                        <span className="text-dark">{query}</span>
                        <button
                          className="btn btn-sm btn-outline ms-2"
                          onClick={() => handleAddHistoricalQuery(query)}
                          disabled={addingHistoricalQuery === query}
                          style={{
                            borderColor: "var(--action-primary)",
                            color: "var(--action-primary)",
                          }}
                        >
                          {addingHistoricalQuery === query
                            ? t("common.adding", "Adding...")
                            : `+ ${t("common.add", "Add")}`}
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            )}
          </div>
        </div>
      </main>

      {/* Run History Modal */}
      {showHistoryModal && (
        <div
          className="modal show d-block"
          tabIndex="-1"
          style={{ backgroundColor: "rgba(0,0,0,0.5)", zIndex: 1050 }}
        >
          <div
            className="modal-dialog modal-dialog-centered modal-xl"
            style={{ maxWidth: "1200px" }}
          >
            <div
              className="modal-content"
              style={{
                maxHeight: "90vh",
                display: "flex",
                flexDirection: "column",
              }}
            >
              <div className="modal-header">
                <h5 className="modal-title d-flex align-items-center gap-2 fw-bold text-dark">
                  <i className="bi bi-clock-history text-primary"></i>{" "}
                  {t("promptLibrary.runHistoryTitle", "Prompt Run History")}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={handleCloseHistoryModal}
                  aria-label={t("common.close", "Close")}
                ></button>
              </div>
              <div
                className="modal-body"
                style={{ overflowY: "auto", flex: "1 1 auto" }}
              >
                <div className="p-4 bg-light card-radius border mb-4">
                  <h6 className="fw-bold text-dark mb-2">
                    {t("promptLibrary.promptText", "Prompt:")}
                  </h6>
                  <p className="text-secondary bg-white p-2 rounded border">
                    {historyQuery}
                  </p>

                  <div className="mt-3">
                    <label
                      htmlFor="history-date-select"
                      className="form-label text-secondary fw-semibold mb-2"
                    >
                      {t(
                        "promptLibrary.selectRunDatePrompt",
                        "Select a historical execution date to view the processed report:",
                      )}
                    </label>
                    <select
                      id="history-date-select"
                      className="form-select"
                      style={{ maxWidth: 400 }}
                      value={historySelectedQueryId || ""}
                      disabled={historyTimestampsLoading}
                      onChange={(e) => handleHistoryDateSelect(e.target.value)}
                    >
                      <option value="">
                        {historyTimestampsLoading
                          ? t("historicalGD.loadingDates", "Loading dates...")
                          : t("historicalGD.selectDatePlaceholder", "Select a date...")}
                      </option>
                      {historyTimestamps?.map((item) => (
                        <option key={item.query_id} value={item.query_id}>
                          {new Date(item.query_timestamp).toLocaleDateString(
                            i18n.resolvedLanguage || i18n.language,
                          )}
                        </option>
                      ))}
                    </select>
                  </div>

                  {historyTimestampsError && (
                    <p className="text-danger mt-2 mb-0">
                      {t("historicalGD.errorLoadingDates", {
                        message: historyTimestampsError.message,
                        defaultValue: `Error loading execution dates: ${historyTimestampsError.message}`,
                      })}
                    </p>
                  )}

                  {!historyTimestampsLoading &&
                    historyTimestamps?.length === 0 && (
                      <p className="text-secondary mt-2 mb-0">
                        {t(
                          "promptLibrary.noRunDatesFound",
                          "No historical execution dates found for this prompt.",
                        )}
                      </p>
                    )}
                </div>

                {historyResultLoading && (
                  <div className="d-flex flex-column align-items-center my-5">
                    <div
                      className="spinner-border"
                      role="status"
                      style={{ color: "var(--brand-color-dark)" }}
                    >
                      <span className="visually-hidden">
                        {t("common.loading", "Loading...")}
                      </span>
                    </div>
                    <p className="mt-2 text-secondary">
                      {t(
                        "promptLibrary.loadingRunHistory",
                        "Loading historical report...",
                      )}
                    </p>
                  </div>
                )}

                {historyResultError && (
                  <p className="text-danger my-3">
                    {t("historicalGD.errorLoadingResult", {
                      message: historyResultError.message,
                      defaultValue: `Error loading report: ${historyResultError.message}`,
                    })}
                  </p>
                )}

                {historyResult && (
                  <div className="p-3 bg-white card-radius border">
                    <div className="mb-3 pb-3 border-bottom">
                      <h6 className="fw-bold text-dark mb-1">
                        {t("promptLibrary.executionDetails", "Execution Details:")}
                      </h6>
                      <p className="text-secondary mb-0 small">
                        {t("historicalQR.queriedOn", {
                          date: new Date(
                            historyResult.data.query_timestamp,
                          ).toLocaleString(i18n.resolvedLanguage || i18n.language),
                          defaultValue: `Queried on ${new Date(
                            historyResult.data.query_timestamp,
                          ).toLocaleString(i18n.resolvedLanguage || i18n.language)}`,
                        })}
                      </p>
                    </div>
                    <QueryResearchResult
                      filteredQueryResearchData={historyResult}
                    />
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={handleCloseHistoryModal}
                >
                  {t("common.close", "Close")}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && (
        <div
          className="modal show d-block"
          tabIndex="-1"
          style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  {t("promptLibrary.editPrompt", "Edit Prompt")}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={handleCloseEditModal}
                  aria-label={t("common.close", "Close")}
                ></button>
              </div>
              <div className="modal-body">
                <div className="mb-3">
                  <label htmlFor="editQuery" className="form-label">
                    {t("promptLibrary.promptText", "Prompt")}
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    id="editQuery"
                    value={editFormData.query}
                    onChange={(e) =>
                      handleEditFormChange("query", e.target.value)
                    }
                  />
                </div>
                <div className="mb-3 row g-3">
                  <div className="col-6">
                    <label className="form-label mb-2">
                      {t("promptLibrary.isBranded", "Branded")}
                    </label>
                    <CustomDropdown
                      options={[
                        { value: "true", label: t("promptLibrary.branded", "Branded") },
                        { value: "false", label: t("promptLibrary.nonBranded", "Non-Branded") },
                      ]}
                      value={editFormData.is_branded}
                      onChange={(v) => handleEditFormChange("is_branded", v)}
                      triggerStyle={{ width: "100%" }}
                    />
                  </div>
                  <div className="col-6">
                    <label className="form-label mb-2">
                      {t("promptLibrary.funnelStage", "Funnel Stage")}
                    </label>
                    <CustomDropdown
                      options={[
                        { value: "top", label: t("tracking.topFunnel", "Top") },
                        { value: "middle", label: t("tracking.middleFunnel", "Middle") },
                        { value: "bottom", label: t("tracking.bottomFunnel", "Bottom") },
                      ]}
                      value={editFormData.funnel_stage}
                      onChange={(v) => handleEditFormChange("funnel_stage", v)}
                      triggerStyle={{ width: "100%" }}
                    />
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={handleCloseEditModal}
                >
                  {t("common.cancel", "Cancel")}
                </button>
                <button
                  type="button"
                  className="btn"
                  onClick={handleSaveEdit}
                  disabled={isEditing}
                  style={{
                    backgroundColor: "var(--action-primary)",
                    color: "white",
                    borderColor: "var(--action-primary)",
                  }}
                >
                  {isEditing ? (
                    <>
                      <span
                        className="spinner-border spinner-border-sm me-2"
                        role="status"
                        aria-hidden="true"
                      ></span>
                      {t("common.editing", "Updating...")}
                    </>
                  ) : (
                    t("userProfile.saveChanges", "Save Changes")
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add Queries Modal */}
      {showAddModal && (
        <div
          className="modal show d-block"
          tabIndex="-1"
          style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  {t("promptLibrary.addPrompts", "Add Prompts")}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={handleCloseAddModal}
                  aria-label={t("common.close", "Close")}
                ></button>
              </div>
              <div className="modal-body">
                <div className="mb-3">
                  <label className="form-label">
                    {t("promptLibrary.selectMethod", "Select Method")}
                  </label>
                  <div className="btn-group w-100" role="group">
                    <input
                      type="radio"
                      className="btn-check"
                      name="addMethod"
                      id="methodText"
                      autoComplete="off"
                      checked={addMethod === "text"}
                      onChange={() => setAddMethod("text")}
                    />
                    <label
                      className="btn btn-outline-primary"
                      htmlFor="methodText"
                      style={{
                        borderColor: "var(--action-primary)",
                        color:
                          addMethod === "text"
                            ? "white"
                            : "var(--action-primary)",
                        backgroundColor:
                          addMethod === "text"
                            ? "var(--action-primary)"
                            : "transparent",
                      }}
                    >
                      {t("promptLibrary.textInput", "Text Input")}
                    </label>

                    <input
                      type="radio"
                      className="btn-check"
                      name="addMethod"
                      id="methodCsv"
                      autoComplete="off"
                      checked={addMethod === "csv"}
                      onChange={() => setAddMethod("csv")}
                    />
                    <label
                      className="btn btn-outline-primary"
                      htmlFor="methodCsv"
                      style={{
                        borderColor: "var(--action-primary)",
                        color:
                          addMethod === "csv"
                            ? "white"
                            : "var(--action-primary)",
                        backgroundColor:
                          addMethod === "csv"
                            ? "var(--action-primary)"
                            : "transparent",
                      }}
                    >
                      {t("promptLibrary.uploadCsv", "Upload CSV")}
                    </label>
                  </div>
                </div>

                {addMethod === "text" ? (
                  <div className="mb-3">
                    <label htmlFor="newQueries" className="form-label">
                      {t("promptLibrary.enterPromptsOnePerLine", "Enter Prompts (one per line)")}
                    </label>
                    <textarea
                      className="form-control"
                      id="newQueries"
                      rows="8"
                      placeholder="Enter prompts separated by newline&#10;Example:&#10;best laptops 2024&#10;how to learn python&#10;digital marketing tips"
                      value={newQueriesText}
                      onChange={(e) => setNewQueriesText(e.target.value)}
                    />
                  </div>
                ) : (
                  <div className="mb-3">
                    <label htmlFor="csvFile" className="form-label">
                      {t("promptLibrary.uploadCsv", "Upload CSV File")}
                    </label>
                    <input
                      type="file"
                      className="form-control"
                      id="csvFile"
                      accept=".csv"
                      onChange={handleCsvFileChange}
                    />
                    <small className="text-muted">
                      {t("promptLibrary.csvPromptColumnHint", "CSV should contain a 'Prompt' column")}
                    </small>
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={handleCloseAddModal}
                >
                  {t("common.cancel", "Cancel")}
                </button>
                <button
                  type="button"
                  className="btn btn-success"
                  onClick={handleAddQueries}
                  disabled={isAdding}
                  style={{
                    backgroundColor: "var(--btn-solid-bg)",
                    color: "var(--btn-solid-text)",
                    borderColor: "var(--btn-solid-border)",
                  }}
                >
                  {isAdding ? (
                    <>
                      <span
                        className="spinner-border spinner-border-sm me-2"
                        role="status"
                        aria-hidden="true"
                      ></span>
                      {t("common.adding", "Adding...")}
                    </>
                  ) : (
                    t("promptLibrary.addPrompts", "Add Prompts")
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div
          className="modal show d-block"
          tabIndex="-1"
          style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
          role="dialog"
          aria-modal="true"
          aria-label={t("promptLibrary.confirmDelete", "Confirm Delete")}
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  {t("promptLibrary.confirmDelete", "Confirm Delete")}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={handleCloseDeleteConfirm}
                  aria-label={t("common.close", "Close")}
                ></button>
              </div>
              <div className="modal-body">
                <p className="mb-2">
                  {t(
                    "promptLibrary.deleteSelectedPromptConfirm",
                    "Are you sure you want to delete these queries?",
                  )}
                </p>
                <ul
                  className="list-group"
                  style={{ maxHeight: "240px", overflowY: "auto" }}
                >
                  {getSelectedQueryDetails().map((query) => (
                    <li
                      key={query.generated_query_id}
                      className="list-group-item"
                    >
                      {query.query}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={handleCloseDeleteConfirm}
                >
                  {t("common.cancel", "Cancel")}
                </button>
                <button
                  type="button"
                  className="btn btn-danger"
                  onClick={handleConfirmDelete}
                  disabled={isDeleting}
                >
                  {isDeleting ? (
                    <>
                      <span
                        className="spinner-border spinner-border-sm me-2"
                        role="status"
                        aria-hidden="true"
                      ></span>
                      {t("common.deleting", "Deleting...")}
                    </>
                  ) : (
                    t("common.delete", "Delete")
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default EditQueryPage;
